import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Sidebar from './components/dashboard/Sidebar';
import DashboardStyles from './components/dashboard/DashboardStyles';
import { GridTexture } from './components/ui/primitives';
import { restoreAuthAfterCOI } from './utils/crossOriginIsolation';
import { startSessionRefresh } from './services/api';
import { TOOL_PATHS, LEGACY_PATH_REDIRECTS, conversionPath, CONTACT_PATH } from './utils/conversionRoutes';
import { BLOG_BASE } from './utils/blogRoutes';
import { DASHBOARD_BLOG_BASE } from './components/dashboard/blog/paths';
import { DashboardThemeProvider, useDashboardTheme } from './theme/DashboardThemeContext';
import { SidebarCollapseProvider, useSidebarCollapse } from './theme/SidebarCollapseContext';
import usePageTracking from './hooks/usePageTracking';
import { setAnalyticsUser } from './utils/analytics';

// Every route below is its own chunk rather than a static import — the
// unauthenticated bundle (landing pages, free tools, login) previously
// pulled in the whole authenticated app (StockVideoEditor's Remotion Player,
// TemplatePreview, etc.) into the same main.js a first-time visitor had to
// download before seeing anything.
// Old /stock-workflows links and bookmarks land on the same page under /workflows.
const LegacyWorkflowRedirect = () => {
    const { pathname, search } = useLocation();
    return <Navigate to={pathname.replace(/^\/stock-workflows/, '/workflows') + search} replace />;
};

const Home = lazy(() => import('./components/dashboard/Home'));
const ImageGenerator = lazy(() => import('./components/dashboard/ImageGenerator'));
const BackgroundRemover = lazy(() => import('./components/dashboard/BackgroundRemover'));
const VideoWebCrawl = lazy(() => import('./components/dashboard/VideoWebCrawl'));
const SrtGenerator = lazy(() => import('./components/dashboard/SrtGenerator'));
const TalkingHead = lazy(() => import('./components/dashboard/TalkingHead'));
const AiAvatar = lazy(() => import('./components/dashboard/AiAvatar'));
const AvatarStudio = lazy(() => import('./components/dashboard/AvatarStudio'));
const DashboardBlogIndex = lazy(() => import('./components/dashboard/blog/DashboardBlogIndex'));
const DashboardBlogPost = lazy(() => import('./components/dashboard/blog/DashboardBlogPost'));
const StockWorkflows = lazy(() => import('./components/dashboard/stock/StockWorkflows'));
const ScreenWriter = lazy(() => import('./components/dashboard/ScreenWriter'));
const SpeechGenerator = lazy(() => import('./components/dashboard/SpeechGenerator'));
// One component, two routes — the Music and Footage pages differ only by which
// library panel they mount.
const AssetLibrary = lazy(() => import('./components/dashboard/AssetLibrary'));
const StockWorkflowDetail = lazy(() => import('./components/dashboard/stock/StockWorkflowDetail'));
const StockVideoEditor = lazy(() => import('./components/dashboard/stock/StockVideoEditor'));
const Credits = lazy(() => import('./components/dashboard/Credits'));
const Login = lazy(() => import('./components/Login'));
const LandingPage = lazy(() => import('./components/landing/LandingPage'));
const FreeTools = lazy(() => import('./components/landing/FreeTools'));
const Contact = lazy(() => import('./components/landing/Contact'));
const Convert = lazy(() => import('./components/free-tools/Convert'));
const FormatGuide = lazy(() => import('./components/free-tools/FormatGuide'));
const BlogIndex = lazy(() => import('./components/blog/BlogIndex'));
const BlogPost = lazy(() => import('./components/blog/BlogPost'));
const SubtitleGenerator = lazy(() => import('./components/free-tools/SubtitleGenerator'));
const ClipVideo = lazy(() => import('./components/free-tools/ClipVideo'));
const Compress = lazy(() => import('./components/free-tools/Compress'));
const GenerateSitemap = lazy(() => import('./components/free-tools/GenerateSitemap'));
const TemplatePreview = lazy(() => import('./components/dashboard/TemplatePreview'));
const AllWorkflows = lazy(() => import('./components/dashboard/AllWorkflows'));

// /convert/jpg-to-png -> /free-tools/convert/jpg-to-png, keeping the slug.
// A plain <Navigate> cannot express this because the target depends on the
// matched parameter.
const LegacyConversionRedirect = () => {
    const { conversionType } = useParams();
    return <Navigate to={conversionPath(conversionType)} replace />;
};

// /free-tools/compress -> the type page.
//
// ?type=video was how the tab used to be selected, and those links exist in the
// wild, so it is honoured once and then normalised to a path. Anything else
// lands on images, which is the more common intent and keeps the bare URL
// working rather than 404ing.
const CompressRoute = () => {
    const [searchParams] = useSearchParams();
    const target =
        searchParams.get('type') === 'video'
            ? TOOL_PATHS.compressVideo
            : TOOL_PATHS.compressImage;
    return <Navigate to={target} replace />;
};

// usePageTracking needs useLocation(), which only works inside <Router>. App renders
// the Router itself, so the hook is mounted through this tiny child rather than moving
// the whole tree. Renders nothing.
const PageTracker = () => {
    usePageTracking();
    return null;
};

const RouteFallback = () => (
    <div className="flex min-h-screen items-center justify-center bg-ink">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-volt" />
    </div>
);

function DashboardShell({ user, onUserRefresh }) {
    // Access flags come from the backend's login and /auth/me responses.
    const isAdmin = Boolean(user?.is_admin);
    const { theme, vars } = useDashboardTheme();
    const { collapsed } = useSidebarCollapse();

    return (
        // flex-row at EVERY size. This used to be flex-col below md, which was
        // correct while the mobile sidebar was an off-canvas drawer (`fixed`,
        // so it took no layout space). Now that below md it is the permanent
        // icon rail sitting in the flex row, a column direction stacks the rail
        // on top of the content instead of beside it.
        <div className="subscene-dashboard relative flex h-screen flex-row bg-ink">
            <DashboardStyles />

            {/* Sidebar */}
            {/* z-50: this wrapper is `relative`, which makes it its own
                stacking context — Sidebar's internal z-40/z-50 (mobile panel,
                menu button) only rank against siblings, not the whole page.
                <main> below is also `relative z-10` and comes later in DOM
                order, so at equal z-index it would stack ON TOP, trapping the
                popped-out mobile sidebar behind page content. This has to
                outrank <main>'s z-10 for the mobile slide-out to be visible.
                Deliberately OUTSIDE the theme scope below — Sidebar always
                stays dark regardless of the dashboard light/dark toggle,
                since the --color-* override only lands on the sibling wrapper. */}
            {/* Width is duplicated on Sidebar's own panel and must stay in
                agreement with it — see SidebarCollapseContext for why neither
                can own the state alone. */}
            {/* w-0 with md overrides: below md the sidebar takes NO layout
                width — a phone screen is too narrow to spend 64px on permanent
                chrome. There its trigger is a floating button and its panel is
                `absolute` over the content, both painted from inside this box.
                Hence w-0 rather than `hidden`, which would remove them too.
                From md the width IS the layout and pushes the content across.
                No overflow-hidden here: everything the sidebar paints on mobile
                is wider than this zero-width box. */}
            <div className={`relative z-50 w-0 flex-shrink-0 transition-[width] duration-300 ease-in-out ${
                collapsed ? 'md:w-16' : 'md:w-64'
            }`}>
                <Sidebar user={user} onUserRefresh={onUserRefresh} />
            </div>

            {/* Main Content Area — one flat, uniform grid pinned behind the
                whole shell (pages scroll over it), rather than a per-page
                grid that would drift out of alignment as content scrolls.
                data-theme/style here (not on the .subscene-dashboard root
                above) is what scopes the light theme to main content only —
                Sidebar is a sibling, never a descendant of this div, so the
                --color-* variable override can't reach it.
                bg-ink here is deliberate and needed: the outer root above
                also carries a (permanently dark) bg-ink of its own, which
                would otherwise show through this wrapper in light mode,
                since without its own background this div is transparent. */}
            <div
                // min-w-0: a flex child defaults to min-width:auto, so wide
                // content (the NLE timeline, code blocks) would widen this
                // column and push the rail off-screen instead of scrolling
                // inside it. Only load-bearing now that the row layout applies
                // on mobile too, where the content is widest relative to the
                // viewport.
                className="dashboard-main-content relative flex min-w-0 flex-1 flex-col overflow-hidden bg-ink"
                data-theme={theme}
                style={vars}
            >
                {/* fade=true (GridTexture's default) applies the same radial
                    mask the landing page's .section-grid uses — solid near
                    the top, fading out toward the bottom/edges — so the
                    dashboard's grid reads the same way. Theme-agnostic (the
                    mask is pure shape, no color), so it applies identically
                    in light and dark without any extra wiring. */}
                <GridTexture size={64} opacity={0.55} color="var(--color-line-grid)" />

                {/* Main Content */}
                <main className="scrollbar-thin relative z-10 flex-1 overflow-y-auto px-5 pb-6 pt-6 sm:px-6 sm:pb-6 md:pt-8">
                    <Suspense fallback={<RouteFallback />}>
                        <Routes>
                            <Route path="/home" element={<Home />} />
                            <Route path="/credits" element={<Credits />} />
                            <Route path="/image-gen" element={isAdmin ? <ImageGenerator /> : <Navigate to="/workflows" replace />} />
                            <Route path="/background-remover" element={isAdmin ? <BackgroundRemover /> : <Navigate to="/workflows" replace />} />
                            <Route path="/video-web-crawl" element={isAdmin ? <VideoWebCrawl /> : <Navigate to="/workflows" replace />} />
                            <Route path="/srt-generator" element={isAdmin ? <SrtGenerator /> : <Navigate to="/workflows" replace />} />
                            <Route path="/talking-head" element={isAdmin ? <TalkingHead /> : <Navigate to="/workflows" replace />} />
                            <Route path="/ai-avatar" element={<Navigate to="/ai-avatar/video" replace />} />
                            <Route path="/ai-avatar/video" element={<AiAvatar />} />
                            <Route path="/ai-avatar/avatars" element={<AvatarStudio />} />
                            <Route path="/template-preview" element={isAdmin ? <TemplatePreview /> : <Navigate to="/workflows" replace />} />
                            <Route path="/all-workflows" element={isAdmin ? <AllWorkflows /> : <Navigate to="/workflows" replace />} />
                            <Route path="/workflows" element={<StockWorkflows isAdmin={isAdmin} />} />
                            <Route path="/screenwriter" element={<ScreenWriter />} />
                            <Route path="/speech-generator" element={<SpeechGenerator />} />
                            <Route path="/assets/music" element={<AssetLibrary kind="music" />} />
                            <Route path="/assets/footage" element={<AssetLibrary kind="footage" />} />
                            <Route path="/assets/images" element={<AssetLibrary kind="images" />} />
                            {/* The guides, rendered inside the shell. A signed-in
                                user cannot reach the public /blogs route — this
                                tree replaces the public one — so the content is
                                mounted here rather than linking out of the app. */}
                            <Route path={DASHBOARD_BLOG_BASE} element={<DashboardBlogIndex />} />
                            <Route path={`${DASHBOARD_BLOG_BASE}/:slug`} element={<DashboardBlogPost />} />
                            <Route path="/stock-workflows/*" element={<LegacyWorkflowRedirect />} />
                            <Route path="/workflows/:workflowUuid/editor" element={<StockVideoEditor />} />
                            <Route path="/workflows/:workflowUuid" element={<StockWorkflowDetail />} />
                            {/* Same reasoning as the dashboard blog routes
                                above: this tree REPLACES the public one, and
                                the catch-all below sends everything else to
                                /home — so without this a signed-in user
                                following a /contact link would bounce to the
                                dashboard instead of reaching the page. */}
                            <Route path={CONTACT_PATH} element={<Contact />} />
                            <Route path="*" element={<Navigate to="/home" replace />} />
                        </Routes>
                    </Suspense>
                </main>
            </div>
        </div>
    );
}

function AppContent({ user, onUserRefresh }) {
    return (
        <DashboardThemeProvider>
            {/* Inside <Router> (see AppContent's call site) because the
                provider reads useLocation to auto-collapse on editor routes. */}
            <SidebarCollapseProvider>
                <DashboardShell user={user} onUserRefresh={onUserRefresh} />
            </SidebarCollapseProvider>
        </DashboardThemeProvider>
    );
}

// Read the stored session SYNCHRONOUSLY, during the first render.
//
// This used to run in a useEffect with `loading` starting at true, which meant
// the first render was always the spinner. That is invisible in a pure SPA, but
// it breaks hydration of prerendered HTML: the prerenderer captures the real
// page (its effects have run), the client boots into a spinner, and React 18
// throws the mismatch away and re-renders — the flash that gets misattributed
// to the prerenderer. localStorage is synchronous, so there is nothing to wait
// for; reading it in a useState initializer makes the first client render match
// the prerendered markup for the logged-out case, which is the one crawlers see.
const readStoredSession = () => {
    try {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        if (token && storedUser) {
            return { user: JSON.parse(storedUser), isAuthenticated: true };
        }
    } catch (err) {
        // Corrupt entry (or localStorage unavailable, e.g. privacy mode) — drop
        // it and continue logged out rather than blocking the render.
        console.error('Error parsing stored user:', err);
        try {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        } catch (_) { /* localStorage unavailable — nothing to clean up */ }
    }
    return { user: null, isAuthenticated: false };
};

function App() {
    // Restoring a COI reload has to happen before the session is read, since it
    // is what moves the token back out of sessionStorage. Runs at module-eval
    // time of the initializer, i.e. still before the first paint.
    const [initialSession] = useState(() => {
        restoreAuthAfterCOI();
        return readStoredSession();
    });
    const [isAuthenticated, setIsAuthenticated] = useState(initialSession.isAuthenticated);
    const [user, setUser] = useState(initialSession.user);

    // Keep the session alive while the app is open. The backend's token lasts
    // 48h; this renews it every 6h (and when a slept tab wakes), so an active
    // user is never dropped at the login screen mid-task. Only runs while
    // authenticated, and stops as soon as that ends.
    useEffect(() => {
        if (!isAuthenticated) return undefined;
        return startSessionRefresh();
    }, [isAuthenticated]);

    const handleLoginSuccess = (userData, token) => {
        setUser(userData);
        setIsAuthenticated(true);
        // The Supabase UUID only — never the email, which is also on userData.
        setAnalyticsUser(userData?.user_id);
    };

    // Identify a RESTORED session too, not just a fresh login: most visits reuse the
    // token in localStorage and never touch handleLoginSuccess, so without this the
    // majority of authenticated traffic would report as anonymous.
    useEffect(() => {
        if (initialSession.isAuthenticated) {
            setAnalyticsUser(initialSession.user?.user_id);
        }
    }, [initialSession]);

    const signedInLanding = (
        <Suspense fallback={<RouteFallback />}>
            <LandingPage isLoggedIn />
        </Suspense>
    );

    // No loading gate: the session is resolved synchronously in the state
    // initializer above, so there is never a frame where auth is unknown.

    return (
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <Router>
                <PageTracker />
                {!isAuthenticated ? (
                    <Suspense fallback={<RouteFallback />}>
                        <Routes>
                            <Route path="/" element={<LandingPage />} />
                            <Route path="/landing" element={<LandingPage />} />
                            {/* Public, and also routed for signed-in users
                                below — a support address is not something to
                                make someone log out to find. */}
                            <Route path={CONTACT_PATH} element={<Contact />} />
                            {/* The free tools live under one base path, so the
                                hub is the parent of its tools rather than a
                                sibling. Paths come from TOOL_PATHS so the
                                router, the sitemap and the prerenderer cannot
                                disagree about what exists. */}
                            <Route path={TOOL_PATHS.hub} element={<FreeTools />} />
                            <Route path={TOOL_PATHS.convert} element={<Convert />} />
                            <Route path={`${TOOL_PATHS.convert}/:conversionType`} element={<Convert />} />
                            <Route path={TOOL_PATHS.formatGuide} element={<FormatGuide />} />
                            <Route path={TOOL_PATHS.subtitles} element={<SubtitleGenerator />} />
                            <Route path={TOOL_PATHS.clipVideo} element={<ClipVideo />} />
                            {/* /compress is the chooser; the two type pages
                                are what target a search. A bare /compress keeps
                                working and defaults to images, so old links and
                                the hub link never dead-end. */}
                            <Route path={TOOL_PATHS.compress} element={<CompressRoute />} />
                            <Route path={TOOL_PATHS.compressImage} element={<Compress variant="image" />} />
                            <Route path={TOOL_PATHS.compressVideo} element={<Compress variant="video" />} />

                            {/* Guides. Public and prerendered like the tools —
                                see utils/blogRoutes.js for the path, which the
                                router, sitemap and prerenderer all read. */}
                            <Route path={BLOG_BASE} element={<BlogIndex />} />
                            <Route path={`${BLOG_BASE}/:slug`} element={<BlogPost />} />

                            {/* Legacy root-level paths. server.js 301s these in
                                production; these client-side redirects cover an
                                in-app navigation that still points at an old
                                path, so nothing dead-ends either way. */}
                            {Object.entries(LEGACY_PATH_REDIRECTS).map(([from, to]) => (
                                <Route key={from} path={from} element={<Navigate to={to} replace />} />
                            ))}
                            <Route path="/convert/:conversionType" element={<LegacyConversionRedirect />} />
                            <Route path="/generate-sitemap" element={<GenerateSitemap />} />
                            <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Suspense>
                ) : (
                    <Routes>
                        {/* The product page stays reachable when signed in, outside
                            the dashboard shell; its button leads to /home. */}
                        <Route path="/" element={signedInLanding} />
                        <Route path="/landing" element={signedInLanding} />
                        <Route path="*" element={<AppContent user={user} onUserRefresh={setUser} />} />
                    </Routes>
                )}
            </Router>
        </GoogleOAuthProvider>
    );
}

export default App;
